/**
 * Sawyer 08 meds-feeding (A1, US5) — the Meds & feeding sheet on paper, for the
 * clipboard the way Gingr's medication report is used.
 *
 * Print mechanics are the run-card deck's and the morning board's: the sheet
 * renders through a PORTAL to document.body marked `data-print-root`, and
 * styles/print.css hides every other body child under @media print, so no
 * nav, toolbar or app shell prints. Flat black on white (the documented
 * print exception to the token rule). For today and past days each dose
 * ends with its latest status, name and time; where nothing is recorded the
 * cell is an empty box for hand initials (the #553 PACFA paper bridge: the
 * paper grid stays until PACFA answers).
 */
import { useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import type { CareDayResponse } from '../../lib/meds-feeding-api.js';
import { formatClock, formatSlots } from '../../lib/care-labels.js';
import { doseStatusText, indexCareDay, longDateLabel, mealLine, prnText, serviceLabel, visitTag } from './care-day-format.js';
import { medTimesText } from './med-line.js';
import '../../styles/print.css';
import './meds-feeding.css';

function facilityToday(iso: string, timeZone: string): string {
  try {
    return new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date(iso));
  } catch {
    return iso.slice(0, 10);
  }
}

const FILTER_LABEL = { all: 'All dogs', boarding: 'Boarding', daycare: 'Daycare' } as const;

export function MedsFeedingPrintLayout({
  data,
  onClose,
  autoPrint = false,
}: {
  data: CareDayResponse;
  onClose: () => void;
  autoPrint?: boolean;
}): JSX.Element {
  const idx = useMemo(() => indexCareDay(data), [data]);
  const tz = data.timeZone;
  // Statuses print for today and past days; a future day has nothing yet.
  const showStatus = data.isToday || data.date < facilityToday(data.generatedAt, tz);

  useEffect(() => {
    if (!autoPrint) return;
    const id = window.requestAnimationFrame(() => window.print());
    return () => window.cancelAnimationFrame(id);
  }, [autoPrint]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const printedAt = formatClock(new Date().toISOString(), tz);

  const sheet = (
    <div className="ferro-print-overlay ferro-mf-print" data-print-root="true" data-testid="meds-feeding-print-root">
      <div className="ferro-print-toolbar">
        <strong>Meds &amp; feeding · {longDateLabel(data.date)}</strong>
        <div className="ferro-print-toolbar__actions">
          <button type="button" className="ferro-btn ferro-btn--ghost" onClick={onClose} data-testid="meds-feeding-print-close">
            Close
          </button>
          <button type="button" className="ferro-btn ferro-btn--primary" onClick={() => window.print()}>
            Print
          </button>
        </div>
      </div>
      <div className="ferro-mf-print__doc">
        <header className="ferro-mf-print__header">
          <div>
            {data.facilityName ? <p className="ferro-mf-print__facility">{data.facilityName}</p> : null}
            <h1 className="ferro-mf-print__title">Meds &amp; feeding</h1>
            <p className="ferro-mf-print__date">
              {longDateLabel(data.date)} · {FILTER_LABEL[data.service]}
            </p>
          </div>
          <p className="ferro-mf-print__printed">Printed {printedAt}</p>
        </header>

        {data.dogs.length === 0 ? <p>No dogs are booked in for {longDateLabel(data.date)}.</p> : null}

        {data.notesOnly.length + data.unscheduledMeds.length > 0 ? (
          <section className="ferro-mf-print__group">
            <h2 className="ferro-mf-print__group-title">Medication notes — times not set</h2>
            {data.notesOnly.map((dogId) => {
              const d = idx.dogs.get(dogId);
              return d ? (
                <div key={`n-${dogId}`} className="ferro-mf-print__entry">
                  <p className="ferro-mf-print__dog">{d.name}</p>
                  <p>{d.medicationNotes}</p>
                </div>
              ) : null;
            })}
            {data.unscheduledMeds.map(({ dogId, medicationId }) => {
              const d = idx.dogs.get(dogId);
              const m = idx.meds.get(medicationId);
              return d && m ? (
                <div key={`u-${medicationId}`} className="ferro-mf-print__entry">
                  <p className="ferro-mf-print__dog">{d.name}</p>
                  <p>
                    {[m.name, m.dosage].filter(Boolean).join(' ')} — {medTimesText(m)}
                  </p>
                </div>
              ) : null;
            })}
          </section>
        ) : null}

        {data.groups.map((g) => (
          <section key={g.slot} className="ferro-mf-print__group">
            <h2 className="ferro-mf-print__group-title">{g.label}</h2>
            {g.entries.length === 0 ? <p className="ferro-mf-print__none">Nothing due.</p> : null}
            {g.entries.map((e) => {
              const d = idx.dogs.get(e.dogId);
              if (!d) return null;
              return (
                <div key={`${g.slot}-${e.dogId}`} className="ferro-mf-print__entry">
                  <p className="ferro-mf-print__dog">
                    {d.name}
                    <span className="ferro-mf-print__meta">
                      {' '}
                      {[d.breed, serviceLabel(d.service), visitTag(d, tz)].filter(Boolean).join(' · ')}
                    </span>
                  </p>
                  {d.allergies ? <p className="ferro-mf-print__allergy">Allergies: {d.allergies}</p> : null}
                  {e.meal ? <p>{mealLine(d, e.meal)}</p> : null}
                  {e.doses.map((dose) => {
                    const m = idx.meds.get(dose.medicationId);
                    if (!m) return null;
                    const status = g.slot === 'as_needed' ? prnText(dose.status, tz) : doseStatusText(dose.status, tz);
                    return (
                      <div key={dose.medicationId} className="ferro-mf-print__dose">
                        <span>
                          {[m.name, m.dosage].filter(Boolean).join(' ')}
                          {m.instructions ? ` — ${m.instructions}` : ''}
                        </span>
                        <span className="ferro-mf-print__cell">{showStatus && status ? status : ''}</span>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </section>
        ))}

        {(data.atHomeMeds ?? []).length > 0 ? (
          <section className="ferro-mf-print__group">
            <h2 className="ferro-mf-print__group-title">Given at home</h2>
            {(data.atHomeMeds ?? []).map(({ dogId, medicationId, slots }) => {
              const d = idx.dogs.get(dogId);
              const m = idx.meds.get(medicationId);
              return d && m ? (
                <div key={`h-${medicationId}`} className="ferro-mf-print__entry">
                  <p className="ferro-mf-print__dog">{d.name}</p>
                  <p>
                    {[m.name, m.dosage].filter(Boolean).join(' ')} — {formatSlots(slots)}, not due during this visit
                  </p>
                </div>
              ) : null;
            })}
          </section>
        ) : null}
      </div>
    </div>
  );

  return createPortal(sheet, document.body);
}
